import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Search, Layers, Building2, ImageOff, Star, X, Copy, Trash2, Download, AlertTriangle, CheckCircle, XCircle, Clock, Filter, MoreVertical, Eye, ArrowUpDown, Package, Edit, ExternalLink, Ban, Plus, Sparkles, Link2, ChevronRight } from 'lucide-react';
import { Toggle } from '../../components/ui/Toggle';
import { ProductionStatus, productionStages, getStageConfig, productionRecords, getProductionRecord, getMemberById, teamMembers, teamRoleConfig, getBuilderProductionSummary, productionPhaseConfig, type ProductionPhase, type BuilderProductionSummary, type TeamMember, type ProductionRecord } from '../../data/productionData';
// --- Types ---
type OptionStatus = 'active' | 'disabled' | 'draft';
type AdminOption = {
  id: string;
  name: string;
  builderId: string;
  builderName: string;
  category: string;
  subcategory: string;
  variantSet: string;
  price: number;
  status: OptionStatus;
  isStandard: boolean;
  hasImage: boolean;
  imageUrl?: string;
  description: string;
  modelsAssigned: number;
  communitiesAssigned: number;
  totalModels: number;
  totalCommunities: number;
  lastUpdated: string;
  overrideCount: number;
  tags: string[];
};
type EnrichedAdminOption = AdminOption & {
  productionRecord?: ProductionRecord;
  productionStatus: ProductionStatus;
  primaryAssignee?: TeamMember;
  isBlocked: boolean;
};
// --- Mock Data ---
export const allOptions: AdminOption[] = [{
  id: 'OPT-001',
  name: 'Black Asphalt Shingles',
  builderId: 'mattamy',
  builderName: 'Mattamy Homes',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  price: 0,
  status: 'active',
  isStandard: true,
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/gy4nLAmMDnxDZrtDgT1xUc/black-asphalt.jpg',
  description: 'Classic black asphalt shingles with 30-year warranty',
  modelsAssigned: 4,
  communitiesAssigned: 6,
  totalModels: 4,
  totalCommunities: 6,
  lastUpdated: '2 days ago',
  overrideCount: 0,
  tags: ['Asphalt', '30-year warranty']
}, {
  id: 'OPT-002',
  name: 'Gray Asphalt Shingles',
  builderId: 'mattamy',
  builderName: 'Mattamy Homes',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  price: 1200,
  status: 'active',
  isStandard: false,
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/7fXbSwM5gVoriK8Hvp5r78/gray-asphalt.jpg',
  description: 'Modern gray asphalt shingles - upgrade option',
  modelsAssigned: 4,
  communitiesAssigned: 5,
  totalModels: 4,
  totalCommunities: 6,
  lastUpdated: '1 week ago',
  overrideCount: 1,
  tags: ['Asphalt', 'Modern']
}, {
  id: 'OPT-003',
  name: 'Stainless Steel Appliances',
  builderId: 'mattamy',
  builderName: 'Mattamy Homes',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  price: 2500,
  status: 'active',
  isStandard: false,
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg',
  description: 'Premium stainless steel appliance package',
  modelsAssigned: 4,
  communitiesAssigned: 6,
  totalModels: 4,
  totalCommunities: 6,
  lastUpdated: '3 days ago',
  overrideCount: 2,
  tags: ['Appliances', 'Stainless']
}, {
  id: 'OPT-004',
  name: 'Natural Oak Hardwood',
  builderId: 'mattamy',
  builderName: 'Mattamy Homes',
  category: 'Interior',
  subcategory: 'Flooring',
  variantSet: 'Hardwood',
  price: 8500,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Classic natural oak hardwood flooring',
  modelsAssigned: 3,
  communitiesAssigned: 4,
  totalModels: 4,
  totalCommunities: 6,
  lastUpdated: '5 days ago',
  overrideCount: 1,
  tags: ['Hardwood', 'Oak']
}, {
  id: 'OPT-005',
  name: 'White Quartz Countertops',
  builderId: 'mattamy',
  builderName: 'Mattamy Homes',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  price: 4500,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Classic white quartz countertops',
  modelsAssigned: 4,
  communitiesAssigned: 6,
  totalModels: 4,
  totalCommunities: 6,
  lastUpdated: '1 week ago',
  overrideCount: 1,
  tags: ['Quartz', 'Classic']
}, {
  id: 'OPT-006',
  name: 'Laminate Countertops',
  builderId: 'mattamy',
  builderName: 'Mattamy Homes',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  price: 0,
  status: 'active',
  isStandard: true,
  hasImage: false,
  description: 'Standard laminate countertops - included',
  modelsAssigned: 4,
  communitiesAssigned: 6,
  totalModels: 4,
  totalCommunities: 6,
  lastUpdated: '2 weeks ago',
  overrideCount: 0,
  tags: ['Laminate', 'Standard']
}, {
  id: 'OPT-007',
  name: 'Charcoal Asphalt Shingles',
  builderId: 'excel',
  builderName: 'Excel Homes',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  price: 0,
  status: 'active',
  isStandard: true,
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/gy4nLAmMDnxDZrtDgT1xUc/black-asphalt.jpg',
  description: 'Charcoal asphalt shingles with 25-year warranty',
  modelsAssigned: 3,
  communitiesAssigned: 4,
  totalModels: 3,
  totalCommunities: 4,
  lastUpdated: '1 day ago',
  overrideCount: 0,
  tags: ['Asphalt', '25-year warranty']
}, {
  id: 'OPT-008',
  name: 'Stainless Steel Appliances',
  builderId: 'excel',
  builderName: 'Excel Homes',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  price: 2800,
  status: 'active',
  isStandard: false,
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg',
  description: 'Premium stainless steel appliance package',
  modelsAssigned: 3,
  communitiesAssigned: 4,
  totalModels: 3,
  totalCommunities: 4,
  lastUpdated: '4 days ago',
  overrideCount: 1,
  tags: ['Appliances', 'Stainless']
}, {
  id: 'OPT-009',
  name: 'Vinyl Plank Flooring',
  builderId: 'excel',
  builderName: 'Excel Homes',
  category: 'Interior',
  subcategory: 'Flooring',
  variantSet: 'Vinyl',
  price: 3200,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Luxury vinyl plank flooring - waterproof',
  modelsAssigned: 2,
  communitiesAssigned: 3,
  totalModels: 3,
  totalCommunities: 4,
  lastUpdated: '6 days ago',
  overrideCount: 0,
  tags: ['Vinyl', 'Waterproof']
}, {
  id: 'OPT-010',
  name: 'Granite Countertops',
  builderId: 'excel',
  builderName: 'Excel Homes',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  price: 5200,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Natural granite countertops - premium upgrade',
  modelsAssigned: 3,
  communitiesAssigned: 4,
  totalModels: 3,
  totalCommunities: 4,
  lastUpdated: '1 week ago',
  overrideCount: 0,
  tags: ['Granite', 'Premium']
}, {
  id: 'OPT-011',
  name: 'Cedar Shake Shingles',
  builderId: 'schell',
  builderName: 'Schell Brothers',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  price: 4800,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Premium cedar shake roofing',
  modelsAssigned: 2,
  communitiesAssigned: 2,
  totalModels: 5,
  totalCommunities: 3,
  lastUpdated: '3 days ago',
  overrideCount: 0,
  tags: ['Cedar', 'Premium']
}, {
  id: 'OPT-012',
  name: 'Built-in Appliances',
  builderId: 'schell',
  builderName: 'Schell Brothers',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  price: 9200,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Premium built-in appliance package with panel-ready fronts',
  modelsAssigned: 5,
  communitiesAssigned: 3,
  totalModels: 5,
  totalCommunities: 3,
  lastUpdated: '2 days ago',
  overrideCount: 3,
  tags: ['Appliances', 'Built-in', 'Premium']
}, {
  id: 'OPT-013',
  name: 'Marble Countertops',
  builderId: 'schell',
  builderName: 'Schell Brothers',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  price: 12000,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Italian marble countertops - luxury upgrade',
  modelsAssigned: 3,
  communitiesAssigned: 2,
  totalModels: 5,
  totalCommunities: 3,
  lastUpdated: '1 week ago',
  overrideCount: 0,
  tags: ['Marble', 'Luxury', 'Italian']
}, {
  id: 'OPT-014',
  name: 'Engineered Hardwood',
  builderId: 'brookfield',
  builderName: 'Brookfield Residential',
  category: 'Interior',
  subcategory: 'Flooring',
  variantSet: 'Hardwood',
  price: 6800,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Engineered hardwood flooring - scratch resistant',
  modelsAssigned: 4,
  communitiesAssigned: 5,
  totalModels: 6,
  totalCommunities: 5,
  lastUpdated: '4 days ago',
  overrideCount: 2,
  tags: ['Hardwood', 'Engineered']
}, {
  id: 'OPT-015',
  name: 'White Vinyl Siding',
  builderId: 'brookfield',
  builderName: 'Brookfield Residential',
  category: 'Exterior',
  subcategory: 'Siding',
  variantSet: 'Vinyl',
  price: 0,
  status: 'disabled',
  isStandard: true,
  hasImage: false,
  description: 'Discontinued by manufacturer',
  modelsAssigned: 0,
  communitiesAssigned: 0,
  totalModels: 6,
  totalCommunities: 5,
  lastUpdated: '2 weeks ago',
  overrideCount: 0,
  tags: ['Vinyl', 'Discontinued']
}, {
  id: 'OPT-016',
  name: 'Porcelain Tile Flooring',
  builderId: 'brookfield',
  builderName: 'Brookfield Residential',
  category: 'Interior',
  subcategory: 'Flooring',
  variantSet: 'Tile',
  price: 5500,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'Large format porcelain tile - bathroom & kitchen',
  modelsAssigned: 6,
  communitiesAssigned: 5,
  totalModels: 6,
  totalCommunities: 5,
  lastUpdated: '3 days ago',
  overrideCount: 1,
  tags: ['Porcelain', 'Tile']
}, {
  id: 'OPT-017',
  name: 'Smart Home Package',
  builderId: 'pulte',
  builderName: 'Pulte Homes',
  category: 'Interior',
  subcategory: 'Technology',
  variantSet: 'Smart Home',
  price: 3500,
  status: 'draft',
  isStandard: false,
  hasImage: false,
  description: 'Smart thermostat, locks, and lighting control',
  modelsAssigned: 0,
  communitiesAssigned: 0,
  totalModels: 4,
  totalCommunities: 3,
  lastUpdated: '1 day ago',
  overrideCount: 0,
  tags: ['Smart Home', 'Technology']
}, {
  id: 'OPT-018',
  name: 'Solar Panel Package',
  builderId: 'pulte',
  builderName: 'Pulte Homes',
  category: 'Exterior',
  subcategory: 'Energy',
  variantSet: 'Solar',
  price: 15000,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: '10kW solar panel system with battery backup',
  modelsAssigned: 2,
  communitiesAssigned: 1,
  totalModels: 4,
  totalCommunities: 3,
  lastUpdated: '5 days ago',
  overrideCount: 0,
  tags: ['Solar', 'Energy', 'Green']
}, {
  id: 'OPT-019',
  name: 'The Lawson',
  builderId: 'mattamy',
  builderName: 'Mattamy Homes',
  category: 'Models',
  subcategory: 'Single Family',
  variantSet: 'Floor Plans',
  price: 599000,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'The Lawson - 2,400 sq ft single family home with 4 bedrooms',
  modelsAssigned: 1,
  communitiesAssigned: 3,
  totalModels: 1,
  totalCommunities: 6,
  lastUpdated: '1 week ago',
  overrideCount: 2,
  tags: ['Single Family', '4 Bed', '2400 sqft']
}, {
  id: 'OPT-020',
  name: 'The Brookstone',
  builderId: 'excel',
  builderName: 'Excel Homes',
  category: 'Models',
  subcategory: 'Townhome',
  variantSet: 'Floor Plans',
  price: 425000,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'The Brookstone - 1,800 sq ft townhome with 3 bedrooms',
  modelsAssigned: 1,
  communitiesAssigned: 2,
  totalModels: 1,
  totalCommunities: 4,
  lastUpdated: '3 days ago',
  overrideCount: 0,
  tags: ['Townhome', '3 Bed', '1800 sqft']
}, {
  id: 'OPT-021',
  name: 'The Summit',
  builderId: 'schell',
  builderName: 'Schell Brothers',
  category: 'Models',
  subcategory: 'Single Family',
  variantSet: 'Floor Plans',
  price: 750000,
  status: 'active',
  isStandard: false,
  hasImage: false,
  description: 'The Summit - 3,200 sq ft luxury single family home',
  modelsAssigned: 1,
  communitiesAssigned: 2,
  totalModels: 1,
  totalCommunities: 3,
  lastUpdated: '5 days ago',
  overrideCount: 1,
  tags: ['Single Family', 'Luxury', '5 Bed', '3200 sqft']
}];
const builderColors: Record<string, string> = {
  mattamy: 'bg-cyan-100 text-cyan-800',
  excel: 'bg-violet-100 text-violet-800',
  schell: 'bg-amber-100 text-amber-800',
  brookfield: 'bg-emerald-100 text-emerald-800',
  pulte: 'bg-rose-100 text-rose-800'
};
export const statusConfig: Record<OptionStatus, {
  label: string;
  variant: 'success' | 'error' | 'neutral';
  icon: typeof CheckCircle;
}> = {
  active: {
    label: 'Active',
    variant: 'success',
    icon: CheckCircle
  },
  disabled: {
    label: 'Disabled',
    variant: 'error',
    icon: XCircle
  },
  draft: {
    label: 'Draft',
    variant: 'neutral',
    icon: Clock
  }
};
// --- Create Option Modal (Admin) ---
function AdminCreateOptionModal({
  isOpen,
  onClose,
  existingOptions,
  builders
}: {
  isOpen: boolean;
  onClose: () => void;
  existingOptions: AdminOption[];
  builders: {
    id: string;
    name: string;
  }[];
}) {
  const [step, setStep] = useState<'search' | 'form'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExisting, setSelectedExisting] = useState<AdminOption | null>(null);
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [variantSet, setVariantSet] = useState('');
  const [price, setPrice] = useState('0');
  const [isStandard, setIsStandard] = useState(false);
  const [builderId, setBuilderId] = useState(builders[0]?.id || '');
  const matches = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return existingOptions.filter(o => o.name.toLowerCase().includes(q) || o.description.toLowerCase().includes(q) || o.tags.some(t => t.toLowerCase().includes(q))).slice(0, 6);
  }, [searchQuery, existingOptions]);
  const exactMatch = matches.find(m => m.name.toLowerCase() === searchQuery.toLowerCase());
  if (!isOpen) return null;
  const handleSelectExisting = (option: AdminOption) => {
    setSelectedExisting(option);
    setName(option.name);
    setDescription(option.description);
    setCategory(option.category);
    setSubcategory(option.subcategory);
    setVariantSet(option.variantSet);
    setPrice(option.price.toString());
    setIsStandard(option.isStandard);
    setBuilderId(option.builderId);
    setStep('form');
  };
  const handleCreateNew = () => {
    setSelectedExisting(null);
    setName(searchQuery);
    setDescription('');
    setCategory('');
    setSubcategory('');
    setVariantSet('');
    setPrice('0');
    setIsStandard(false);
    setBuilderId(builders[0]?.id || '');
    setStep('form');
  };
  const handleSave = () => {
    // In a real app, this would POST to the API
    setStep('search');
    setSearchQuery('');
    setSelectedExisting(null);
    onClose();
  };
  const handleBack = () => {
    setStep('search');
    setSelectedExisting(null);
  };
  const existingCategories = Array.from(new Set(existingOptions.map(o => o.category)));
  const existingSubcategories = Array.from(new Set(existingOptions.filter(o => o.category === category).map(o => o.subcategory)));
  const existingVariantSets = Array.from(new Set(existingOptions.filter(o => o.category === category && o.subcategory === subcategory).map(o => o.variantSet)));
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1475">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-1476">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-1477">
          <div data-id="element-1478">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-1479">
              {step === 'search' ? 'Add Option' : selectedExisting ? 'Duplicate Existing Option' : 'Create New Option'}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1480">
              {step === 'search' ? 'Search existing options or create a new one' : selectedExisting ? 'This option exists — you can duplicate it for another builder' : 'This will create a new option in the global database'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-id="element-1481">
            <X className="h-5 w-5" data-id="element-1482" />
          </button>
        </div>

        {step === 'search' ? <div className="p-6 space-y-4" data-id="element-1483">
            <div data-id="element-1484">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1485">
                Option Name
              </label>
              <div className="relative" data-id="element-1486">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-1487" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Start typing to search or create..." className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm" autoFocus data-id="element-1488" />
              </div>
            </div>

            {searchQuery.trim() && <div className="space-y-2" data-id="element-1489">
                {matches.length > 0 && <div data-id="element-1490">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-1491">
                      Existing Options
                    </p>
                    <div className="space-y-1.5" data-id="element-1492">
                      {matches.map(option => <button key={option.id} onClick={() => handleSelectExisting(option)} className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left group" data-id="element-1493">
                          <div className="h-10 w-10 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-1494">
                            {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt="" className="h-full w-full object-cover" data-id="element-1495" /> : <ImageOff className="h-4 w-4 text-slate-300" data-id="element-1496" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1497">
                            <div className="flex items-center gap-2" data-id="element-1498">
                              <span className="font-medium text-slate-900 text-sm group-hover:text-primary-700 truncate" data-id="element-1499">
                                {option.name}
                              </span>
                              {option.isStandard && <Star className="h-3 w-3 text-emerald-500 fill-emerald-500 flex-shrink-0" data-id="element-1500" />}
                            </div>
                            <p className="text-xs text-slate-500 truncate" data-id="element-1501">
                              {option.builderName} · {option.category} ›{' '}
                              {option.subcategory}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0" data-id="element-1502">
                            {option.isStandard ? <span className="text-xs font-medium text-emerald-600" data-id="element-1503">
                                Included
                              </span> : <span className="text-sm font-medium text-slate-900" data-id="element-1504">
                                ${option.price.toLocaleString()}
                              </span>}
                          </div>
                          <Link2 className="h-4 w-4 text-slate-300 group-hover:text-primary-500 flex-shrink-0" data-id="element-1505" />
                        </button>)}
                    </div>
                  </div>}

                {!exactMatch && <div data-id="element-1506">
                    {matches.length > 0 && <div className="relative my-3" data-id="element-1507">
                        <div className="absolute inset-0 flex items-center" data-id="element-1508">
                          <div className="w-full border-t border-slate-200" data-id="element-1509" />
                        </div>
                        <div className="relative flex justify-center" data-id="element-1510">
                          <span className="bg-white px-3 text-xs text-slate-400" data-id="element-1511">
                            or
                          </span>
                        </div>
                      </div>}
                    <button onClick={handleCreateNew} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-primary-400 hover:bg-primary-50/30 transition-all text-left group" data-id="element-1512">
                      <div className="h-10 w-10 rounded-md bg-primary-50 border border-primary-200 flex items-center justify-center flex-shrink-0" data-id="element-1513">
                        <Plus className="h-5 w-5 text-primary-600" data-id="element-1514" />
                      </div>
                      <div className="flex-1" data-id="element-1515">
                        <p className="font-medium text-slate-900 text-sm group-hover:text-primary-700" data-id="element-1516">
                          Create "{searchQuery}"
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-1517">
                          New option — will be added to the global catalog
                        </p>
                      </div>
                      <Sparkles className="h-4 w-4 text-slate-300 group-hover:text-primary-500 flex-shrink-0" data-id="element-1518" />
                    </button>
                  </div>}
              </div>}

            {!searchQuery.trim() && <div className="text-center py-8" data-id="element-1519">
                <Search className="h-10 w-10 text-slate-200 mx-auto mb-3" data-id="element-1520" />
                <p className="text-sm text-slate-500" data-id="element-1521">
                  Type an option name to search or create
                </p>
                <p className="text-xs text-slate-400 mt-1" data-id="element-1522">
                  Search across all builders and categories
                </p>
              </div>}
          </div> : <div className="p-6 space-y-5" data-id="element-1523">
            {selectedExisting && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-id="element-1524">
                <Link2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-1525" />
                <div data-id="element-1526">
                  <p className="text-sm font-medium text-blue-900" data-id="element-1527">
                    Based on existing option
                  </p>
                  <p className="text-xs text-blue-700 mt-0.5" data-id="element-1528">
                    From {selectedExisting.builderName} — you can modify details
                    below.
                  </p>
                </div>
              </div>}

            {!selectedExisting && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-1529">
                <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-1530" />
                <div data-id="element-1531">
                  <p className="text-sm font-medium text-amber-900" data-id="element-1532">
                    Creating new global option
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5" data-id="element-1533">
                    This will be added to the global database.
                  </p>
                </div>
              </div>}

            {/* Builder */}
            <div data-id="element-1534">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1535">
                Builder
              </label>
              <select value={builderId} onChange={e => setBuilderId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-1536">
                {builders.map(b => <option key={b.id} value={b.id} data-id="element-1537">
                    {b.name}
                  </option>)}
              </select>
            </div>

            {/* Name */}
            <div data-id="element-1538">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1539">
                Option Name
              </label>
              <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-1540" />
            </div>

            {/* Description */}
            <div data-id="element-1541">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1542">
                Description
              </label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-1543" />
            </div>

            {/* Category / Subcategory / Variant Set */}
            <div className="grid grid-cols-3 gap-3" data-id="element-1544">
              <div data-id="element-1545">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1546">
                  Category
                </label>
                <input type="text" value={category} onChange={e => setCategory(e.target.value)} list="admin-category-list" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="e.g. Interior" data-id="element-1547" />
                <datalist id="admin-category-list" data-id="element-1548">
                  {existingCategories.map(c => <option key={c} value={c} data-id="element-1549" />)}
                </datalist>
              </div>
              <div data-id="element-1550">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1551">
                  Subcategory
                </label>
                <input type="text" value={subcategory} onChange={e => setSubcategory(e.target.value)} list="admin-subcategory-list" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="e.g. Kitchen" data-id="element-1552" />
                <datalist id="admin-subcategory-list" data-id="element-1553">
                  {existingSubcategories.map(s => <option key={s} value={s} data-id="element-1554" />)}
                </datalist>
              </div>
              <div data-id="element-1555">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1556">
                  Variant Set
                </label>
                <input type="text" value={variantSet} onChange={e => setVariantSet(e.target.value)} list="admin-variantset-list" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="e.g. Countertops" data-id="element-1557" />
                <datalist id="admin-variantset-list" data-id="element-1558">
                  {existingVariantSets.map(v => <option key={v} value={v} data-id="element-1559" />)}
                </datalist>
              </div>
            </div>

            {/* Price */}
            <div data-id="element-1560">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1561">
                Base Price
              </label>
              <div className="relative" data-id="element-1562">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-1563">
                  $
                </span>
                <input type="number" value={price} onChange={e => setPrice(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" data-id="element-1564" />
              </div>
            </div>

            {/* Standard toggle */}
            <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50" data-id="element-1565">
              <div className="flex items-center justify-between" data-id="element-1566">
                <div data-id="element-1567">
                  <label className="block text-sm font-medium text-slate-900" data-id="element-1568">
                    Include as Standard
                  </label>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-1569">
                    {isStandard ? 'Included at no extra cost' : 'This is an upgrade option'}
                  </p>
                </div>
                <Toggle checked={isStandard} onChange={setIsStandard} label={isStandard ? 'Standard' : 'Upgrade'} data-id="element-1570" />
              </div>
            </div>
          </div>}

        <div className="flex items-center justify-between p-6 border-t border-slate-200 sticky bottom-0 bg-white rounded-b-lg" data-id="element-1571">
          <div data-id="element-1572">
            {step === 'form' && <button onClick={handleBack} className="text-sm text-slate-500 hover:text-slate-700" data-id="element-1573">
                ← Back to search
              </button>}
          </div>
          <div className="flex items-center gap-3" data-id="element-1574">
            <Button variant="ghost" onClick={onClose} data-id="element-1575">
              Cancel
            </Button>
            {step === 'form' && <Button onClick={handleSave} disabled={!name.trim() || !category.trim() || !subcategory.trim() || !variantSet.trim()} leftIcon={selectedExisting ? <Copy className="h-4 w-4" data-id="element-1577" /> : <Plus className="h-4 w-4" data-id="element-1578" />} data-id="element-1576">
                {selectedExisting ? 'Duplicate Option' : 'Create Option'}
              </Button>}
          </div>
        </div>
      </div>
    </div>;
}
// --- Main Page ---
export function AdminOptionsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [builderFilter, setBuilderFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'standard' | 'upgrade'>('all');
  const [imageFilter, setImageFilter] = useState<'all' | 'has-image' | 'missing-image'>('all');
  // New Production Filters
  const [productionStatusFilter, setProductionStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [blockedFilter, setBlockedFilter] = useState<'all' | 'blocked'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<'name' | 'price' | 'builder' | 'lastUpdated' | 'productionStatus'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // Derived data
  const builders = useMemo(() => {
    const map = new Map<string, string>();
    allOptions.forEach(o => map.set(o.builderId, o.builderName));
    return Array.from(map.entries()).map(([id, name]) => ({
      id,
      name
    }));
  }, []);
  const categories = useMemo(() => {
    const order = ['Models', 'Exterior', 'Interior'];
    const cats = Array.from(new Set(allOptions.map(o => o.category)));
    return cats.sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, []);
  const subcategories = useMemo(() => {
    const filtered = categoryFilter !== 'all' ? allOptions.filter(o => o.category === categoryFilter) : allOptions;
    return Array.from(new Set(filtered.map(o => o.subcategory))).sort();
  }, [categoryFilter]);
  // Reset subcategory when category changes
  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setSubcategoryFilter('all');
  };
  // Enrich options with production data
  const enrichedOptions = useMemo<EnrichedAdminOption[]>(() => {
    return allOptions.map(option => {
      const record = getProductionRecord(option.id);
      const pmId = record?.assignments?.pm;
      const firstAssigneeId = record?.assignments ? Object.values(record.assignments)[0] : undefined;
      const assigneeId = pmId || firstAssigneeId;
      const primaryAssignee = assigneeId ? getMemberById(assigneeId) : undefined;
      return {
        ...option,
        productionRecord: record,
        productionStatus: record?.productionStatus || 'not-started',
        primaryAssignee,
        isBlocked: (record?.blockers?.length || 0) > 0
      };
    });
  }, []);
  const filteredOptions = useMemo(() => {
    let result = enrichedOptions;
    if (builderFilter !== 'all') {
      result = result.filter(o => o.builderId === builderFilter);
    }
    if (categoryFilter !== 'all') {
      result = result.filter(o => o.category === categoryFilter);
    }
    if (subcategoryFilter !== 'all') {
      result = result.filter(o => o.subcategory === subcategoryFilter);
    }
    if (statusFilter !== 'all') {
      result = result.filter(o => o.status === statusFilter);
    }
    if (typeFilter === 'standard') {
      result = result.filter(o => o.isStandard);
    } else if (typeFilter === 'upgrade') {
      result = result.filter(o => !o.isStandard);
    }
    if (imageFilter === 'has-image') {
      result = result.filter(o => o.hasImage);
    } else if (imageFilter === 'missing-image') {
      result = result.filter(o => !o.hasImage);
    }
    // Apply Production Filters
    if (productionStatusFilter !== 'all') {
      result = result.filter(o => o.productionStatus === productionStatusFilter);
    }
    if (assigneeFilter !== 'all') {
      result = result.filter(o => o.productionRecord?.assignments && Object.values(o.productionRecord.assignments).includes(assigneeFilter));
    }
    if (blockedFilter === 'blocked') {
      result = result.filter(o => o.isBlocked);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(o => o.name.toLowerCase().includes(q) || o.builderName.toLowerCase().includes(q) || o.category.toLowerCase().includes(q) || o.subcategory.toLowerCase().includes(q) || o.description.toLowerCase().includes(q) || o.id.toLowerCase().includes(q) || o.tags.some(t => t.toLowerCase().includes(q)));
    }
    result.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);else if (sortField === 'price') cmp = a.price - b.price;else if (sortField === 'builder') cmp = a.builderName.localeCompare(b.builderName);else if (sortField === 'productionStatus') {
        const indexA = productionStages.findIndex(s => s.key === a.productionStatus);
        const indexB = productionStages.findIndex(s => s.key === b.productionStatus);
        cmp = indexA - indexB;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [enrichedOptions, builderFilter, categoryFilter, subcategoryFilter, statusFilter, typeFilter, imageFilter, productionStatusFilter, assigneeFilter, blockedFilter, searchQuery, sortField, sortDir]);
  // Stats
  const stats = useMemo(() => {
    const total = enrichedOptions.length;
    const builderCount = new Set(enrichedOptions.map(o => o.builderId)).size;
    const missingImages = enrichedOptions.filter(o => !o.hasImage).length;
    const active = enrichedOptions.filter(o => o.status === 'active').length;
    const withOverrides = enrichedOptions.filter(o => o.overrideCount > 0).length;
    // Production Stats
    const inProduction = enrichedOptions.filter(o => {
      const phase = getStageConfig(o.productionStatus).phase;
      return phase === 'development' || phase === 'qa';
    }).length;
    const blocked = enrichedOptions.filter(o => o.isBlocked).length;
    const nameGroups = new Map<string, number[]>();
    enrichedOptions.forEach(o => {
      const prices = nameGroups.get(o.name) || [];
      prices.push(o.price);
      nameGroups.set(o.name, prices);
    });
    const conflicts = Array.from(nameGroups.values()).filter(prices => prices.length > 1 && new Set(prices).size > 1).length;
    return {
      total,
      builderCount,
      missingImages,
      active,
      withOverrides,
      conflicts,
      inProduction,
      blocked
    };
  }, [enrichedOptions]);
  // Builder Production Summary
  const builderSummary = useMemo(() => {
    if (builderFilter === 'all') return null;
    const builderOptionIds = enrichedOptions.filter(o => o.builderId === builderFilter).map(o => o.id);
    return getBuilderProductionSummary(builderOptionIds);
  }, [builderFilter, enrichedOptions]);
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredOptions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredOptions.map(o => o.id)));
    }
  };
  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const activeFilterCount = [builderFilter !== 'all', categoryFilter !== 'all', subcategoryFilter !== 'all', statusFilter !== 'all', typeFilter !== 'all', imageFilter !== 'all', productionStatusFilter !== 'all', assigneeFilter !== 'all', blockedFilter !== 'all'].filter(Boolean).length;
  const clearFilters = () => {
    setBuilderFilter('all');
    setCategoryFilter('all');
    setSubcategoryFilter('all');
    setStatusFilter('all');
    setTypeFilter('all');
    setImageFilter('all');
    setProductionStatusFilter('all');
    setAssigneeFilter('all');
    setBlockedFilter('all');
  };
  return <div className="space-y-6 max-w-[1400px] mx-auto" data-id="element-1579">
      <AdminCreateOptionModal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} existingOptions={allOptions} builders={builders} data-id="element-1580" />

      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1581">
        <div data-id="element-1582">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1583">
            Global Options Manager
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-1584">
            View and manage all options across every builder
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-1585">
          <Button variant="outline" leftIcon={<Download className="h-4 w-4" data-id="element-1587" />} data-id="element-1586">
            Export CSV
          </Button>
          <Button leftIcon={<Plus className="h-4 w-4" data-id="element-1589" />} onClick={() => setCreateModalOpen(true)} data-id="element-1588">
            Add Option
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-1590">
        <Card noPadding data-id="element-1591">
          <div className="p-4" data-id="element-1592">
            <div className="flex items-center justify-between mb-1.5" data-id="element-1593">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1594">
                Total Options
              </p>
              <Package className="h-4 w-4 text-slate-400" data-id="element-1595" />
            </div>
            <p className="text-2xl font-bold text-slate-900" data-id="element-1596">{stats.total}</p>
          </div>
        </Card>
        <Card noPadding data-id="element-1597">
          <div className="p-4" data-id="element-1598">
            <div className="flex items-center justify-between mb-1.5" data-id="element-1599">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1600">
                Builders
              </p>
              <Building2 className="h-4 w-4 text-slate-400" data-id="element-1601" />
            </div>
            <p className="text-2xl font-bold text-slate-900" data-id="element-1602">
              {stats.builderCount}
            </p>
          </div>
        </Card>
        <Card noPadding data-id="element-1603">
          <div className="p-4" data-id="element-1604">
            <div className="flex items-center justify-between mb-1.5" data-id="element-1605">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1606">
                Active
              </p>
              <CheckCircle className="h-4 w-4 text-emerald-500" data-id="element-1607" />
            </div>
            <p className="text-2xl font-bold text-emerald-600" data-id="element-1608">
              {stats.active}
            </p>
          </div>
        </Card>
        <Card noPadding data-id="element-1609">
          <div className="p-4" data-id="element-1610">
            <div className="flex items-center justify-between mb-1.5" data-id="element-1611">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1612">
                In Production
              </p>
              <Clock className="h-4 w-4 text-blue-500" data-id="element-1613" />
            </div>
            <p className="text-2xl font-bold text-blue-600" data-id="element-1614">
              {stats.inProduction}
            </p>
          </div>
        </Card>
        <Card noPadding data-id="element-1615">
          <div className="p-4" data-id="element-1616">
            <div className="flex items-center justify-between mb-1.5" data-id="element-1617">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1618">
                Blocked
              </p>
              <AlertTriangle className="h-4 w-4 text-red-500" data-id="element-1619" />
            </div>
            <p className="text-2xl font-bold text-red-600" data-id="element-1620">{stats.blocked}</p>
          </div>
        </Card>
        <Card noPadding data-id="element-1621">
          <div className="p-4" data-id="element-1622">
            <div className="flex items-center justify-between mb-1.5" data-id="element-1623">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-1624">
                With Overrides
              </p>
              <Layers className="h-4 w-4 text-amber-500" data-id="element-1625" />
            </div>
            <p className="text-2xl font-bold text-amber-600" data-id="element-1626">
              {stats.withOverrides}
            </p>
          </div>
        </Card>
      </div>

      {/* Builder Production Summary Bar */}
      {builderSummary && <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-6 shadow-sm" data-id="element-1627">
          <div className="flex-shrink-0" data-id="element-1628">
            <p className="text-sm font-medium text-slate-900" data-id="element-1629">
              Builder Progress
            </p>
            <p className="text-xs text-slate-500" data-id="element-1630">
              {builderSummary.totalOptions} total options
            </p>
          </div>

          <div className="flex-1 flex items-center gap-4" data-id="element-1631">
            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-1632">
              <div className="h-full bg-emerald-500 transition-all" style={{
            width: `${builderSummary.live / builderSummary.totalOptions * 100}%`
          }} title={`Live: ${builderSummary.live}`} data-id="element-1633" />
              <div className="h-full bg-blue-400 transition-all" style={{
            width: `${builderSummary.inProgress / builderSummary.totalOptions * 100}%`
          }} title={`In Progress: ${builderSummary.inProgress}`} data-id="element-1634" />
              <div className="h-full bg-slate-300 transition-all" style={{
            width: `${builderSummary.notStarted / builderSummary.totalOptions * 100}%`
          }} title={`Not Started: ${builderSummary.notStarted}`} data-id="element-1635" />
            </div>
            <div className="flex items-center gap-4 text-sm flex-shrink-0" data-id="element-1636">
              <div className="flex items-center gap-1.5" data-id="element-1637">
                <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-1638" />
                <span className="text-slate-600" data-id="element-1639">
                  <span className="font-medium text-slate-900" data-id="element-1640">
                    {builderSummary.percentComplete}%
                  </span>{' '}
                  Live
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-1641">
                <div className="w-2 h-2 rounded-full bg-blue-400" data-id="element-1642" />
                <span className="text-slate-600" data-id="element-1643">
                  <span className="font-medium text-slate-900" data-id="element-1644">
                    {builderSummary.inProgress}
                  </span>{' '}
                  In Progress
                </span>
              </div>
              {builderSummary.blocked > 0 && <div className="flex items-center gap-1.5" data-id="element-1645">
                  <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-1646" />
                  <span className="text-red-600 font-medium" data-id="element-1647">
                    {builderSummary.blocked} Blocked
                  </span>
                </div>}
            </div>
          </div>
        </div>}

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && <div className="bg-slate-900 text-white rounded-lg px-4 py-3 flex items-center justify-between" data-id="element-1648">
          <div className="flex items-center gap-3" data-id="element-1649">
            <span className="text-sm font-medium" data-id="element-1650">
              {selectedIds.size} option{selectedIds.size !== 1 ? 's' : ''}{' '}
              selected
            </span>
            <button onClick={() => setSelectedIds(new Set())} className="text-xs text-slate-400 hover:text-white underline" data-id="element-1651">
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2" data-id="element-1652">
            <select className="bg-slate-800 border border-slate-700 text-sm text-slate-200 rounded-md px-3 py-1.5 focus:ring-primary-500 focus:border-primary-500" onChange={e => {
          if (e.target.value) {
            // In a real app, this would trigger a bulk update API call
            console.log(`Moving ${selectedIds.size} items to ${e.target.value}`);
            e.target.value = ''; // Reset select
            setSelectedIds(new Set()); // Clear selection after action
          }
        }} data-id="element-1653">
              <option value="" data-id="element-1654">Move to Stage...</option>
              {Object.entries(productionPhaseConfig).map(([phaseKey, phase]) => <optgroup key={phaseKey} label={phase.label} data-id="element-1655">
                    {productionStages.filter(s => s.phase === phaseKey).map(s => <option key={s.key} value={s.key} data-id="element-1656">
                          {s.label}
                        </option>)}
                  </optgroup>)}
            </select>
            <div className="w-px h-5 bg-slate-700 mx-1" data-id="element-1657" />
            <Button variant="outline" size="sm" className="!border-slate-600 !text-slate-300 hover:!text-white hover:!border-slate-400" data-id="element-1658">
              <Copy className="h-3.5 w-3.5 mr-1.5" data-id="element-1659" /> Duplicate
            </Button>
            <Button variant="outline" size="sm" className="!border-slate-600 !text-slate-300 hover:!text-white hover:!border-slate-400" data-id="element-1660">
              <Download className="h-3.5 w-3.5 mr-1.5" data-id="element-1661" /> Export
            </Button>
            <Button variant="danger" size="sm" data-id="element-1662">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" data-id="element-1663" /> Delete
            </Button>
          </div>
        </div>}

      {/* Table Card */}
      <Card noPadding data-id="element-1664">
        {/* Search + Filter Bar */}
        <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl space-y-3" data-id="element-1665">
          <div className="flex items-center gap-3" data-id="element-1666">
            <div className="flex-1 relative" data-id="element-1667">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-1668" />
              <input type="text" placeholder="Search by name, builder, category, tag, or ID..." className="w-full pl-10 pr-4 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1669" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-md transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-primary-50 border-primary-200 text-primary-700' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`} data-id="element-1670">
              <Filter className="h-4 w-4" data-id="element-1671" />
              Filters
              {activeFilterCount > 0 && <span className="bg-primary-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium" data-id="element-1672">
                  {activeFilterCount}
                </span>}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && <div className="flex items-center gap-3 flex-wrap pt-1" data-id="element-1673">
              <select value={builderFilter} onChange={e => setBuilderFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1674">
                <option value="all" data-id="element-1675">All Builders</option>
                {builders.map(b => <option key={b.id} value={b.id} data-id="element-1676">
                    {b.name}
                  </option>)}
              </select>
              <select value={categoryFilter} onChange={e => handleCategoryChange(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1677">
                <option value="all" data-id="element-1678">All Categories</option>
                {categories.map(c => <option key={c} value={c} data-id="element-1679">
                    {c}
                  </option>)}
              </select>
              <select value={subcategoryFilter} onChange={e => setSubcategoryFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1680">
                <option value="all" data-id="element-1681">All Subcategories</option>
                {subcategories.map(s => <option key={s} value={s} data-id="element-1682">
                    {s}
                  </option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1683">
                <option value="all" data-id="element-1684">All Statuses</option>
                <option value="active" data-id="element-1685">Active</option>
                <option value="disabled" data-id="element-1686">Disabled</option>
                <option value="draft" data-id="element-1687">Draft</option>
              </select>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1688">
                <option value="all" data-id="element-1689">All Types</option>
                <option value="standard" data-id="element-1690">Standard Only</option>
                <option value="upgrade" data-id="element-1691">Upgrades Only</option>
              </select>
              <select value={imageFilter} onChange={e => setImageFilter(e.target.value as any)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1692">
                <option value="all" data-id="element-1693">All Images</option>
                <option value="has-image" data-id="element-1694">Has Image</option>
                <option value="missing-image" data-id="element-1695">Missing Image</option>
              </select>
              <select value={productionStatusFilter} onChange={e => setProductionStatusFilter(e.target.value as any)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1696">
                <option value="all" data-id="element-1697">All Stages</option>
                {Object.entries(productionPhaseConfig).map(([phaseKey, phase]) => <optgroup key={phaseKey} label={phase.label} data-id="element-1698">
                      {productionStages.filter(s => s.phase === phaseKey).map(s => <option key={s.key} value={s.key} data-id="element-1699">
                            {s.label}
                          </option>)}
                    </optgroup>)}
              </select>
              <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1700">
                <option value="all" data-id="element-1701">All Assignees</option>
                {teamMembers.map(m => <option key={m.id} value={m.id} data-id="element-1702">
                    {m.name} ({teamRoleConfig[m.role].shortLabel})
                  </option>)}
              </select>
              <select value={blockedFilter} onChange={e => setBlockedFilter(e.target.value as any)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-1703">
                <option value="all" data-id="element-1704">All Blockers</option>
                <option value="blocked" data-id="element-1705">Blocked Only</option>
              </select>
              {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-1706">
                  Clear all
                </button>}
            </div>}
        </div>

        {/* Results count */}
        <div className="px-4 py-2 border-b border-slate-100 bg-white" data-id="element-1707">
          <p className="text-xs text-slate-500" data-id="element-1708">
            Showing{' '}
            <span className="font-medium text-slate-700" data-id="element-1709">
              {filteredOptions.length}
            </span>{' '}
            of {allOptions.length} options
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-1710">
          <table className="w-full text-left text-sm" data-id="element-1711">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-1712">
              <tr data-id="element-1713">
                <th className="px-4 py-3 w-10" data-id="element-1714">
                  <input type="checkbox" checked={selectedIds.size === filteredOptions.length && filteredOptions.length > 0} onChange={toggleSelectAll} className="rounded border-slate-300 text-primary-500 focus:ring-primary-500" data-id="element-1715" />
                </th>
                <th className="px-4 py-3 w-12" data-id="element-1716"></th>
                <th className="px-4 py-3" data-id="element-1717">
                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-slate-700" data-id="element-1718">
                    Option Name <ArrowUpDown className="h-3 w-3" data-id="element-1719" />
                  </button>
                </th>
                <th className="px-4 py-3" data-id="element-1720">
                  <button onClick={() => handleSort('builder')} className="flex items-center gap-1 hover:text-slate-700" data-id="element-1721">
                    Builder <ArrowUpDown className="h-3 w-3" data-id="element-1722" />
                  </button>
                </th>
                <th className="px-4 py-3" data-id="element-1723">Category</th>
                <th className="px-4 py-3" data-id="element-1724">
                  <button onClick={() => handleSort('price')} className="flex items-center gap-1 hover:text-slate-700" data-id="element-1725">
                    Price <ArrowUpDown className="h-3 w-3" data-id="element-1726" />
                  </button>
                </th>
                <th className="px-4 py-3" data-id="element-1727">Status</th>
                <th className="px-4 py-3" data-id="element-1728">
                  <button onClick={() => handleSort('productionStatus')} className="flex items-center gap-1 hover:text-slate-700" data-id="element-1729">
                    Production <ArrowUpDown className="h-3 w-3" data-id="element-1730" />
                  </button>
                </th>
                <th className="px-4 py-3" data-id="element-1731">Owner</th>
                <th className="px-4 py-3" data-id="element-1732">Assigned To</th>
                <th className="px-4 py-3" data-id="element-1733">Updated</th>
                <th className="px-4 py-3 text-right" data-id="element-1734">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1735">
              {filteredOptions.length === 0 ? <tr data-id="element-1736">
                  <td colSpan={12} className="px-6 py-16 text-center" data-id="element-1737">
                    <div className="flex flex-col items-center gap-2" data-id="element-1738">
                      <Package className="h-8 w-8 text-slate-300" data-id="element-1739" />
                      <p className="text-slate-500 font-medium" data-id="element-1740">
                        No options found
                      </p>
                      <p className="text-sm text-slate-400" data-id="element-1741">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </td>
                </tr> : filteredOptions.map(option => {
              const StatusIcon = statusConfig[option.status].icon;
              const prodStage = getStageConfig(option.productionStatus);
              return <tr key={option.id} className={`transition-colors ${selectedIds.has(option.id) ? 'bg-primary-50/50' : 'hover:bg-slate-50'}`} data-id="element-1742">
                      <td className="px-4 py-3" data-id="element-1743">
                        <input type="checkbox" checked={selectedIds.has(option.id)} onChange={() => toggleSelect(option.id)} className="rounded border-slate-300 text-primary-500 focus:ring-primary-500" data-id="element-1744" />
                      </td>
                      <td className="px-4 py-3" data-id="element-1745">
                        <div className="h-9 w-9 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-1746">
                          {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt="" className="h-full w-full object-cover" data-id="element-1747" /> : <ImageOff className="h-4 w-4 text-slate-300" data-id="element-1748" />}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1749">
                        <button onClick={() => navigate(`/admin/options/${option.id}`)} className="text-left group" data-id="element-1750">
                          <div className="flex items-center gap-2" data-id="element-1751">
                            <span className="font-medium text-slate-900 group-hover:text-primary-600 transition-colors" data-id="element-1752">
                              {option.name}
                            </span>
                            {option.isStandard && <Star className="h-3.5 w-3.5 text-emerald-500 fill-emerald-500 flex-shrink-0" data-id="element-1753" />}
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5" data-id="element-1754">
                            {option.subcategory} › {option.variantSet}
                          </p>
                        </button>
                      </td>
                      <td className="px-4 py-3" data-id="element-1755">
                        <div className="flex items-center gap-2" data-id="element-1756">
                          <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${builderColors[option.builderId] || 'bg-slate-100 text-slate-600'}`} data-id="element-1757">
                            {option.builderName.charAt(0)}
                          </div>
                          <span className="text-slate-700 text-xs" data-id="element-1758">
                            {option.builderName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1759">
                        <div data-id="element-1760">
                          <span className="text-slate-600" data-id="element-1761">
                            {option.category}
                          </span>
                          <p className="text-xs text-slate-400" data-id="element-1762">
                            {option.subcategory}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1763">
                        {option.isStandard ? <span className="text-emerald-600 font-medium text-xs" data-id="element-1764">
                            Included
                          </span> : <span className="font-medium text-slate-900" data-id="element-1765">
                            ${option.price.toLocaleString()}
                          </span>}
                        {option.overrideCount > 0 && <span className="ml-1.5 text-[10px] text-amber-600 font-medium" data-id="element-1766">
                            +{option.overrideCount}
                          </span>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1767">
                        <Badge variant={statusConfig[option.status].variant} className="text-[11px]" data-id="element-1768">
                          <StatusIcon className="h-3 w-3 mr-1" data-id="element-1769" />
                          {statusConfig[option.status].label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-1770">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border ${prodStage.bgColor} ${prodStage.color} ${prodStage.borderColor}`} data-id="element-1771">
                          <div className={`w-1.5 h-1.5 rounded-full ${prodStage.dotColor}`} data-id="element-1772" />
                          {prodStage.shortLabel}
                          {option.productionStatus === 'internal-qa' && option.productionRecord?.qaLevel && <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded bg-violet-200/60 text-violet-800" data-id="element-1773">
                                R{option.productionRecord.qaLevel}
                              </span>}
                        </div>
                        {option.isBlocked && <div className="mt-1 flex items-center gap-1 text-[10px] text-red-600 font-medium" data-id="element-1774">
                            <AlertTriangle className="h-3 w-3" data-id="element-1775" /> Blocked
                          </div>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1776">
                        {option.primaryAssignee ? <div className="flex items-center gap-2" data-id="element-1777">
                            <div className={`h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${option.primaryAssignee.avatarColor}`} data-id="element-1778">
                              {option.primaryAssignee.initials}
                            </div>
                            <span className="text-xs text-slate-700 font-medium whitespace-nowrap" data-id="element-1779">
                              {option.primaryAssignee.name}
                            </span>
                          </div> : <span className="text-xs text-slate-400" data-id="element-1780">—</span>}
                      </td>
                      <td className="px-4 py-3" data-id="element-1781">
                        <div className="text-xs text-slate-600 space-y-0.5" data-id="element-1782">
                          <div data-id="element-1783">
                            <span className="font-medium" data-id="element-1784">
                              {option.modelsAssigned}
                            </span>
                            <span className="text-slate-400" data-id="element-1785">
                              /{option.totalModels} models
                            </span>
                          </div>
                          <div data-id="element-1786">
                            <span className="font-medium" data-id="element-1787">
                              {option.communitiesAssigned}
                            </span>
                            <span className="text-slate-400" data-id="element-1788">
                              /{option.totalCommunities} comm.
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1789">
                        <span className="text-xs text-slate-500" data-id="element-1790">
                          {option.lastUpdated}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-1791">
                        <div className="flex items-center justify-end gap-1" data-id="element-1792">
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/options/${option.id}`)} data-id="element-1793">
                            <Eye className="h-3.5 w-3.5" data-id="element-1794" />
                          </Button>
                          <div className="relative" data-id="element-1795">
                            <Button variant="ghost" size="sm" onClick={() => setOpenMenuId(openMenuId === option.id ? null : option.id)} data-id="element-1796">
                              <MoreVertical className="h-3.5 w-3.5" data-id="element-1797" />
                            </Button>
                            {openMenuId === option.id && <>
                                <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)} data-id="element-1798" />
                                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" data-id="element-1799">
                                  <button onClick={() => {
                            navigate(`/admin/options/${option.id}`);
                            setOpenMenuId(null);
                          }} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-1800">
                                    <Eye className="h-4 w-4 text-slate-400" data-id="element-1801" />
                                    View Details
                                  </button>
                                  <button onClick={() => setOpenMenuId(null)} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-1802">
                                    <Edit className="h-4 w-4 text-slate-400" data-id="element-1803" />
                                    Edit Option
                                  </button>
                                  <button onClick={() => setOpenMenuId(null)} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-1804">
                                    <Copy className="h-4 w-4 text-slate-400" data-id="element-1805" />
                                    Duplicate
                                  </button>
                                  <button onClick={() => setOpenMenuId(null)} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-1806">
                                    <ExternalLink className="h-4 w-4 text-slate-400" data-id="element-1807" />
                                    Open in Builder
                                  </button>
                                  <div className="border-t border-slate-100 my-1" data-id="element-1808" />
                                  <div className="relative group/stage" data-id="element-1809">
                                    <button className="flex items-center justify-between w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-1810">
                                      <div className="flex items-center gap-2" data-id="element-1811">
                                        <ArrowUpDown className="h-4 w-4 text-slate-400" data-id="element-1812" />
                                        Move to Stage
                                      </div>
                                      <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-1813" />
                                    </button>
                                    {/* Flyout submenu */}
                                    <div className="invisible group-hover/stage:visible absolute right-full bottom-0 mr-1 w-52 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-[60] max-h-80 overflow-y-auto" data-id="element-1814">
                                      {Object.entries(productionPhaseConfig).map(([phaseKey, phase]) => <div key={phaseKey} data-id="element-1815">
                                          <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-1816">
                                            {phase.label}
                                          </p>
                                          {productionStages.filter(s => s.phase === phaseKey).map(s => {
                                  const isCurrent = s.key === option.productionStatus;
                                  return <button key={s.key} onClick={() => {
                                    console.log(`Moving ${option.id} to ${s.key}`);
                                    setOpenMenuId(null);
                                  }} className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm transition-colors ${isCurrent ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-1817">
                                                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isCurrent ? s.dotColor : 'bg-slate-300'}`} data-id="element-1818" />
                                                  {s.label}
                                                  {isCurrent && <span className="ml-auto text-[10px] text-blue-500 font-medium" data-id="element-1819">
                                                      Current
                                                    </span>}
                                                </button>;
                                })}
                                        </div>)}
                                    </div>
                                  </div>
                                  <div className="border-t border-slate-100 my-1" data-id="element-1820" />
                                  <button onClick={() => setOpenMenuId(null)} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-1821">
                                    <Ban className="h-4 w-4 text-slate-400" data-id="element-1822" />
                                    {option.status === 'active' ? 'Disable' : 'Enable'}
                                  </button>
                                  <button onClick={() => setOpenMenuId(null)} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-1823">
                                    <Trash2 className="h-4 w-4" data-id="element-1824" />
                                    Delete
                                  </button>
                                </div>
                              </>}
                          </div>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl flex items-center justify-between" data-id="element-1825">
          <p className="text-xs text-slate-500" data-id="element-1826">
            {filteredOptions.length} result
            {filteredOptions.length !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2" data-id="element-1827">
            <Button variant="outline" size="sm" disabled data-id="element-1828">
              Previous
            </Button>
            <span className="text-xs text-slate-500 px-2" data-id="element-1829">Page 1 of 1</span>
            <Button variant="outline" size="sm" disabled data-id="element-1830">
              Next
            </Button>
          </div>
        </div>
      </Card>
    </div>;
}